// frontend/src/services/sessionManager.js
import axios from 'axios';
import { getBackendUrl } from '../config/apiConfig';
import toastService from './toastService';
import logger from '../utils/logger';

// Module loaded - debug log removed

/**
 * Session Manager - Activity-based token refresh
 * 
 * Features:
 * - Tracks user activity (mouse, keyboard, API calls)
 * - Automatically refreshes token when user is active
 * - Hard limit: 8 hours from initial login
 * - Warns user before final logout
 */
class SessionManager {
  constructor() {
    this.activityTimeout = null;
    this.refreshInterval = null;
    this.inactivityInterval = null; // Dedicated inactivity check interval
    this.lastActivity = Date.now();
    this.lastStorageUpdate = Date.now(); // Track last time localStorage was updated
    this.isRefreshing = false; // In-memory lock
    this.loginTime = null;
    this.isActive = false;
    
    // Configuration
    this.ACTIVITY_THRESHOLD = 25 * 60 * 1000; // 25 minutes of inactivity before stopping refresh
    this.REFRESH_INTERVAL = 5 * 60 * 1000; // Refresh token every 5 minutes if active
    this.HARD_LIMIT = 8 * 60 * 60 * 1000; // 8 hour hard limit (full work day)
    this.WARNING_TIME = 7.5 * 60 * 60 * 1000; // Warn at 7.5 hours
    this.INACTIVITY_LOGOUT = 30 * 60 * 1000; // Log out after 30 minutes of inactivity
    this.INACTIVITY_WARNING_TIME = 25 * 60 * 1000; // Warn at 25 minutes of inactivity (5 min before logout)
    
    // EDGE CASE: Clock drift buffer (30 seconds)
    // We log out slightly BEFORE the server limit to ensure a graceful experience
    this.CLOCK_DRIFT_BUFFER = 30 * 1000;
    
    // EDGE CASE: Sleep/wake grace period (2 minutes)
    // When a laptop wakes from sleep, give the user a brief chance to interact
    // instead of immediately logging them out
    this.SLEEP_WAKE_GRACE_PERIOD = 2 * 60 * 1000;
    
    this.warningShown = false;
    this.inactivityWarningShown = false;
    this.isLoggingOut = false;
  }

  /**
   * Initialize session manager
   */
  init() {
    logger.debug('SessionManager.init() called');
    
    if (this.isActive) {
      logger.debug('Session manager already initialized, skipping');
      return;
    }

    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    logger.debug('Tokens found:', { token: !!token, refreshToken: !!refreshToken });
    
    if (!token) {
      logger.debug('No token found, session manager not initialized');
      return;
    }
    // Note: refreshToken is optional - inactivity check works without it

    this.isActive = true;
    
    // CRITICAL FIX: Restore login time from localStorage to persist across page reloads
    // This prevents the 8-hour hard limit from resetting on every page refresh
    const storedLoginTime = localStorage.getItem('sessionLoginTime');
    if (storedLoginTime) {
      this.loginTime = parseInt(storedLoginTime, 10);
      logger.debug('Restored login time from localStorage:', new Date(this.loginTime).toISOString());
    } else {
      this.loginTime = Date.now();
      localStorage.setItem('sessionLoginTime', this.loginTime.toString());
      logger.debug('New login time set:', new Date(this.loginTime).toISOString());
    }
    
    // CRITICAL FIX: Restore last activity from localStorage
    // This prevents inactivity timer from resetting on page refresh
    const storedLastActivity = localStorage.getItem('sessionLastActivity');
    if (storedLastActivity) {
      this.lastActivity = parseInt(storedLastActivity, 10);
      logger.debug('Restored last activity from localStorage:', new Date(this.lastActivity).toISOString());
    } else {
      this.lastActivity = Date.now();
      localStorage.setItem('sessionLastActivity', this.lastActivity.toString());
    }
    
    this.warningShown = false;
    
    // CRITICAL FIX: Check session validity IMMEDIATELY on init
    // This catches expired sessions when user returns to a stale tab
    if (this.checkSessionExpiredOnInit()) {
      return; // Session expired, logout already called
    }

    // Set up activity listeners
    this.setupActivityListeners();

    // Set up visibility change listener (for when user returns to tab)
    this.setupVisibilityListener();

    // Set up storage listener (for cross-tab synchronization)
    this.setupStorageListener();

    // Start refresh interval
    this.startRefreshInterval();

    logger.info('Session manager initialized');
  }
  
  /**
   * Check if session is expired on init (before any activity handlers run)
   * Returns true if session is expired and logout was triggered
   */
  checkSessionExpiredOnInit() {
    // Define public pages where session expiration should be silent (no overlay)
    const publicPaths = ['/', '/home', '/login', '/register', '/register2', '/register3', '/register-interest', '/forgot-password', '/reset-password', '/verify-email', '/verify-email-sent', '/pricing', '/terms', '/privacy', '/refund', '/community-guidelines', '/cookie-policy', '/l3v3l-info', '/help'];
    const currentPath = window.location.pathname;
    const isPublicPage = publicPaths.some(path => currentPath === path || currentPath.startsWith(path + '/'));
    
    // Check hard limit first
    const timeSinceLogin = Date.now() - this.loginTime;
    if (timeSinceLogin >= (this.HARD_LIMIT - this.CLOCK_DRIFT_BUFFER)) {
      logger.warn(`Session near or exceeded 8-hour hard limit on init (${Math.round(timeSinceLogin / 3600000)} hours)`);
      
      if (isPublicPage) {
        // On public pages, silently clear tokens without showing overlay
        logger.info('Session expired on public page - silently clearing tokens');
        this.silentLogout('init_hard_limit_public_page');
      } else {
        toastService.warning('Your session has expired (8 hour limit). Please log in again.', 5000);
        this.logout('init_hard_limit_exceeded');
      }
      return true;
    }
    
    // Check inactivity
    const timeSinceActivity = Date.now() - this.lastActivity;
    if (timeSinceActivity >= (this.INACTIVITY_LOGOUT - this.CLOCK_DRIFT_BUFFER)) {
      logger.warn(`Session near or exceeded inactivity limit on init (${Math.round(timeSinceActivity / 60000)} minutes)`);
      
      if (isPublicPage) {
        // On public pages, silently clear tokens without showing overlay
        logger.info('Session expired due to inactivity on public page - silently clearing tokens');
        this.silentLogout('init_inactivity_public_page');
      } else {
        toastService.warning('Your session has expired due to inactivity. Please log in again.', 5000);
        this.logout('init_inactivity_exceeded');
      }
      return true;
    }
    
    return false;
  }

  /**
   * Set up activity listeners
   */
  setupActivityListeners() {
    // Mouse movement
    document.addEventListener('mousemove', this.handleActivity);
    
    // Keyboard activity
    document.addEventListener('keydown', this.handleActivity);
    
    // Click activity
    document.addEventListener('click', this.handleActivity);
    
    // Touch activity (mobile)
    document.addEventListener('touchstart', this.handleActivity);
    
    // Scroll activity
    document.addEventListener('scroll', this.handleActivity);
  }

  /**
   * Set up visibility change listener
   * This checks session validity when user returns to the tab
   */
  setupVisibilityListener() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /**
   * Handle visibility change (user returns to tab)
   * CRITICAL: Check session validity BEFORE any activity handlers can reset values
   */
  handleVisibilityChange = async () => {
    if (document.visibilityState === 'visible' && this.isActive) {
      logger.debug('Tab became visible, checking session...');
      
      // CRITICAL FIX: Read from localStorage to get the ACTUAL last activity time
      // This is important because the in-memory value may have been updated by
      // activity handlers that fire before this visibility change handler
      const storedLastActivity = localStorage.getItem('sessionLastActivity');
      const actualLastActivity = storedLastActivity 
        ? parseInt(storedLastActivity, 10) 
        : this.lastActivity;
      
      const storedLoginTime = localStorage.getItem('sessionLoginTime');
      const actualLoginTime = storedLoginTime 
        ? parseInt(storedLoginTime, 10) 
        : this.loginTime;
      
      // Check hard limit first (no grace period for this)
      const timeSinceLogin = Date.now() - actualLoginTime;
      if (timeSinceLogin >= (this.HARD_LIMIT - this.CLOCK_DRIFT_BUFFER)) {
        logger.warn(`Session near 8-hour hard limit on tab return (${Math.round(timeSinceLogin / 3600000)} hours)`);
        toastService.warning('Your session has expired (8 hour limit). Please log in again.', 5000);
        this.logout('tab_return_hard_limit');
        return;
      }

      // Check if user has been inactive too long
      const timeSinceActivity = Date.now() - actualLastActivity;
      
      if (timeSinceActivity >= (this.INACTIVITY_LOGOUT - this.CLOCK_DRIFT_BUFFER)) {
        // SLEEP/WAKE GRACE PERIOD: If the inactivity is close to the limit (within grace period),
        // this likely means the user's laptop went to sleep. Give them a brief chance to interact
        // instead of immediately logging out. For very long inactivity (well past the grace period),
        // log out immediately since the server-side session is likely already revoked.
        const graceLimitMs = this.INACTIVITY_LOGOUT + this.SLEEP_WAKE_GRACE_PERIOD;
        
        if (timeSinceActivity < graceLimitMs) {
          logger.warn(`Sleep/wake detected: ${Math.round(timeSinceActivity / 60000)} min inactive (within grace period). Attempting recovery...`);
          toastService.warning(
            'Welcome back! Your session was idle while you were away. Refreshing your session...',
            8000
          );
          
          // Try to refresh the token - if server-side session is still valid, we recover
          try {
            await this.refreshToken();
            // Refresh succeeded - session recovered!
            logger.info('Session recovered after sleep/wake via token refresh');
            this.lastActivity = Date.now();
            this.lastStorageUpdate = Date.now();
            localStorage.setItem('sessionLastActivity', this.lastActivity.toString());
            this.inactivityWarningShown = false;
            return;
          } catch (refreshError) {
            // Refresh failed - server already killed the session
            logger.warn('Session recovery failed after sleep/wake, logging out');
            toastService.warning('Your session has expired due to inactivity. Please log in again.', 5000);
            this.logout('tab_return_inactivity_grace_failed');
            return;
          }
        }
        
        // Well past grace period - log out immediately
        logger.warn(`User inactive for ${Math.round(timeSinceActivity / 60000)} minutes (from localStorage), logging out`);
        toastService.warning('Your session has expired due to inactivity. Please log in again.', 5000);
        this.logout('tab_return_inactivity');
        return;
      }
      
      // Show inactivity warning if in the warning zone (25-30 min)
      if (timeSinceActivity >= this.INACTIVITY_WARNING_TIME && !this.inactivityWarningShown) {
        const remainingMin = Math.ceil((this.INACTIVITY_LOGOUT - timeSinceActivity) / 60000);
        toastService.warning(
          `You've been inactive for ${Math.round(timeSinceActivity / 60000)} minutes. ` +
          `Your session will expire in ~${remainingMin} minutes. Move your mouse or press a key to stay logged in.`,
          15000
        );
        this.inactivityWarningShown = true;
      }

      // EDGE CASE: If token is "old" (e.g. interval was throttled), refresh immediately
      const lastUpdate = localStorage.getItem('sessionLastTokenUpdate');
      const now = Date.now();
      if (!lastUpdate || (now - parseInt(lastUpdate, 10) > (this.REFRESH_INTERVAL - 30000))) {
        logger.debug('Token may be stale due to background throttling, refreshing...');
        await this.refreshToken();
      }

      // Reset activity on successful return
      this.lastActivity = Date.now();
      this.lastStorageUpdate = Date.now();
      localStorage.setItem('sessionLastActivity', this.lastActivity.toString());
    }
  }

  /**
   * Remove activity listeners
   */
  removeActivityListeners() {
    document.removeEventListener('mousemove', this.handleActivity);
    document.removeEventListener('keydown', this.handleActivity);
    document.removeEventListener('click', this.handleActivity);
    document.removeEventListener('touchstart', this.handleActivity);
    document.removeEventListener('scroll', this.handleActivity);
    document.removeEventListener('input', this.handleActivity);
    document.removeEventListener('change', this.handleActivity);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /**
   * Set up storage listener
   * This handles cross-tab synchronization of session state
   */
  setupStorageListener() {
    window.addEventListener('storage', this.handleStorageChange);
  }

  /**
   * Handle storage changes from other tabs
   */
  handleStorageChange = (event) => {
    if (!this.isActive) {
      // EDGE CASE: If we are not active (e.g. on Login page) but a token appears,
      // it means the user logged in in another tab.
      if (event.key === 'token' && event.newValue) {
        logger.info('Token detected from another tab, reloading to sync state');
        window.location.href = '/dashboard';
      }
      return;
    }

    if (event.key === 'sessionLastActivity' && event.newValue) {
      const remoteActivity = parseInt(event.newValue, 10);
      if (remoteActivity > this.lastActivity) {
        logger.debug('Session activity synced from another tab:', new Date(remoteActivity).toISOString());
        this.lastActivity = remoteActivity;
      }
    }

    if (event.key === 'sessionLoginTime' && event.newValue) {
      const remoteLogin = parseInt(event.newValue, 10);
      if (this.loginTime !== remoteLogin) {
        logger.debug('Session login time synced from another tab');
        this.loginTime = remoteLogin;
      }
    }
    
    // EDGE CASE: Token or username changed in another tab.
    // Token changes happen routinely during refresh (every 5 min) - NO reload needed.
    // Username changes indicate a different user logged in - MUST reload.
    if (event.key === 'token' && event.newValue && event.oldValue && event.newValue !== event.oldValue) {
      // Token rotated in another tab (normal refresh) - the new token is already
      // in localStorage and will be picked up by axios interceptor on next API call.
      // No page reload needed.
      logger.debug('Token refreshed in another tab, no page reload needed');
      return;
    }
    
    if (event.key === 'username' && event.newValue && event.oldValue && event.newValue !== event.oldValue) {
      // Different user logged in from another tab - must reload to clear stale React state
      logger.warn('Different user detected from another tab, forcing reload');
      window.location.reload();
      return;
    }
    
    // If token was removed in another tab (logout), logout here too
    if (event.key === 'token' && !event.newValue) {
      logger.info('Token removed in another tab, logging out');
      this.logout('token_removed_external');
    }
  }

  /**
   * Handle user activity
   * Throttled to avoid excessive localStorage writes
   */
  handleActivity = () => {
    const now = Date.now();
    
    // DEBUG: Log activity firing (throttled for console)
    if (now - (this._lastLogTime || 0) > 60000) {
      logger.debug('Activity detected (throttled log)');
      this._lastLogTime = now;
    }
    
    this.lastActivity = now;
    
    // Reset inactivity warning when user becomes active again
    if (this.inactivityWarningShown) {
      this.inactivityWarningShown = false;
      logger.debug('Inactivity warning reset - user is active again');
    }
    
    // Throttle: Only update localStorage if more than 30 seconds since last update
    // This prevents excessive writes while ensuring other tabs see the activity
    if (now - this.lastStorageUpdate > 30000) {
      this.lastStorageUpdate = now;
      localStorage.setItem('sessionLastActivity', now.toString());
    }
  }

  /**
   * Check if user is still active
   */
  isUserActive() {
    const timeSinceActivity = Date.now() - this.lastActivity;
    return timeSinceActivity < this.ACTIVITY_THRESHOLD;
  }

  /**
   * Check if session has exceeded hard limit
   */
  hasExceededHardLimit() {
    if (!this.loginTime) return false;
    const timeSinceLogin = Date.now() - this.loginTime;
    return timeSinceLogin >= this.HARD_LIMIT;
  }

  /**
   * Check if we should show warning
   */
  shouldShowWarning() {
    if (!this.loginTime || this.warningShown) return false;
    const timeSinceLogin = Date.now() - this.loginTime;
    return timeSinceLogin >= this.WARNING_TIME;
  }

  /**
   * Start refresh interval
   */
  startRefreshInterval() {
    // Clear existing interval
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }

    // Check and refresh every 5 minutes
    this.refreshInterval = setInterval(async () => {
      await this.checkAndRefresh();
    }, this.REFRESH_INTERVAL);

    logger.debug('Token refresh interval started');
    
    // ALSO start a dedicated inactivity check interval
    // This runs more frequently (every 1 minute) to catch inactivity faster
    // Browsers may throttle this in background tabs, but it will catch up when tab is active
    this.startInactivityCheckInterval();
  }
  
  /**
   * Start dedicated inactivity check interval
   * Runs every minute to check if user has been inactive for too long
   */
  startInactivityCheckInterval() {
    // Clear existing interval
    if (this.inactivityInterval) {
      clearInterval(this.inactivityInterval);
    }
    
    this.inactivityInterval = setInterval(() => {
      if (!this.isActive) return;
      
      // Read from localStorage to get the persisted value (more reliable than in-memory)
      const storedLastActivity = localStorage.getItem('sessionLastActivity');
      const lastActivityTime = storedLastActivity ? parseInt(storedLastActivity, 10) : this.lastActivity;
      
      const timeSinceActivity = Date.now() - lastActivityTime;
      
      // Check if we should log out (30 min - 30 sec buffer)
      if (timeSinceActivity >= (this.INACTIVITY_LOGOUT - this.CLOCK_DRIFT_BUFFER)) {
        logger.warn(`Session near inactivity limit (${Math.round(timeSinceActivity / 60000)} minutes)`);
        toastService.warning('Your session has expired due to inactivity. Please log in again.', 5000);
        this.logout('inactivity_interval_check');
        return;
      }
      
      // Show warning at 25 minutes (5 min before logout)
      if (timeSinceActivity >= this.INACTIVITY_WARNING_TIME && !this.inactivityWarningShown) {
        const remainingMin = Math.ceil((this.INACTIVITY_LOGOUT - timeSinceActivity) / 60000);
        logger.warn(`Inactivity warning: ${Math.round(timeSinceActivity / 60000)} minutes idle, ${remainingMin} min until logout`);
        toastService.warning(
          `You've been inactive for ${Math.round(timeSinceActivity / 60000)} minutes. ` +
          `Your session will expire in ~${remainingMin} minutes. Move your mouse or press a key to stay logged in.`,
          15000
        );
        this.inactivityWarningShown = true;
      }
    }, 60 * 1000); // Check every 1 minute
  }

  /**
   * Check session and refresh if needed
   */
  async checkAndRefresh() {
    try {
      // Read from localStorage for accurate cross-tab values (in-memory can be stale in background tabs)
      const storedLoginTime = localStorage.getItem('sessionLoginTime');
      const effectiveLoginTime = storedLoginTime ? parseInt(storedLoginTime, 10) : this.loginTime;
      const storedLastActivity = localStorage.getItem('sessionLastActivity');
      const effectiveLastActivity = storedLastActivity ? parseInt(storedLastActivity, 10) : this.lastActivity;

      // Check hard limit first
      if (Date.now() - effectiveLoginTime >= (this.HARD_LIMIT - this.CLOCK_DRIFT_BUFFER)) {
        logger.warn('Session near 8-hour hard limit in refresh check');
        toastService.warning('Your session has expired (8 hour limit). Please log in again.', 5000);
        this.logout('refresh_check_hard_limit');
        return;
      }

      // Show warning if approaching limit
      if (this.shouldShowWarning()) {
        const remainingMinutes = Math.ceil((this.HARD_LIMIT - (Date.now() - effectiveLoginTime)) / 60000);
        toastService.warning(`Your session will expire in ${remainingMinutes} minutes. Save your work!`, 10000);
        this.warningShown = true;
      }

      // Check if user has been inactive too long - log them out
      const timeSinceActivity = Date.now() - effectiveLastActivity;
      if (timeSinceActivity >= (this.INACTIVITY_LOGOUT - this.CLOCK_DRIFT_BUFFER)) {
        logger.warn(`User inactive for ${Math.round(timeSinceActivity / 60000)} minutes, logging out`);
        toastService.warning('Your session has expired due to inactivity. Please log in again.', 5000);
        this.logout('refresh_check_inactivity');
        return;
      }

      // Show inactivity warning at 25 minutes (5 min before logout)
      if (timeSinceActivity >= this.INACTIVITY_WARNING_TIME && !this.inactivityWarningShown) {
        const remainingMin = Math.ceil((this.INACTIVITY_LOGOUT - timeSinceActivity) / 60000);
        logger.warn(`Inactivity warning in refresh check: ${Math.round(timeSinceActivity / 60000)} min idle`);
        toastService.warning(
          `You've been inactive for ${Math.round(timeSinceActivity / 60000)} minutes. ` +
          `Your session will expire in ~${remainingMin} minutes. Move your mouse or press a key to stay logged in.`,
          15000
        );
        this.inactivityWarningShown = true;
      }

      // Only refresh if user is active (within 25 min threshold)
      if (!this.isUserActive()) {
        logger.debug('User inactive, skipping token refresh');
        return;
      }

      // Refresh token
      await this.refreshToken();
      
    } catch (error) {
      logger.error('Error in checkAndRefresh:', error);
    }
  }

  /**
   * Refresh access token
   */
  async refreshToken() {
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (!refreshToken) {
      logger.warn('No refresh token found');
      return;
    }

    // EDGE CASE: Prevent multiple tabs from refreshing simultaneously
    // Check if another tab is already refreshing (lock expires after 30s)
    const refreshLock = localStorage.getItem('sessionRefreshLock');
    const now = Date.now();
    if (refreshLock && (now - parseInt(refreshLock, 10) < 30000)) {
      logger.debug('Refresh already in progress in another tab, skipping');
      return;
    }

    // EDGE CASE: Avoid redundant refreshes if token was updated very recently (< 2 mins)
    const lastUpdate = localStorage.getItem('sessionLastTokenUpdate');
    if (lastUpdate && (now - parseInt(lastUpdate, 10) < 120000)) {
      logger.debug('Token was refreshed very recently, skipping');
      return;
    }

    if (this.isRefreshing) return;
    this.isRefreshing = true;
    localStorage.setItem('sessionRefreshLock', now.toString());

    try {
      logger.debug('Refreshing access token...');
      
      const response = await axios.post(
        `${getBackendUrl()}/api/auth/refresh-token`,
        { refresh_token: refreshToken },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data && response.data.access_token) {
        // Update access token
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('sessionLastTokenUpdate', Date.now().toString());
        logger.info('Access token refreshed successfully');
        
        // Reset warning flag if we successfully refreshed
        // (in case user was warned but then became active again)
        if (this.warningShown) {
          const timeSinceLogin = Date.now() - this.loginTime;
          if (timeSinceLogin < this.WARNING_TIME) {
            this.warningShown = false;
          }
        }
      }
    } catch (error) {
      if (error.response?.status === 401) {
        logger.warn('Token refresh failed - session expired (401)');
        
        // Log detailed error for debugging silent timeouts
        if (error.response.data) {
          logger.debug('Refresh error data:', error.response.data);
        }
        
        toastService.warning('Your session has expired. Please log in again.', 4000);
        this.logout('refresh_failed_401');
      } else {
        // EDGE CASE: Network error during refresh should NOT log out immediately
        // unless it's a persistent failure. For now, just log it.
        logger.error('Token refresh error (network?):', error);
      }
    } finally {
      this.isRefreshing = false;
      localStorage.removeItem('sessionRefreshLock');
    }
  }

  /**
   * Logout and cleanup
   * @param {string} reason - Optional reason for logout for logging
   */
  logout(reason = 'unknown') {
    // Prevent multiple logout calls
    if (this.isLoggingOut) {
      return;
    }
    this.isLoggingOut = true;
    
    logger.info(`SessionManager.logout() triggered. Reason: ${reason}`);
    
    // Clear intervals
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
    
    if (this.inactivityInterval) {
      clearInterval(this.inactivityInterval);
      this.inactivityInterval = null;
    }

    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout);
      this.activityTimeout = null;
    }

    // Remove listeners
    this.removeActivityListeners();
    window.removeEventListener('storage', this.handleStorageChange);

    // Clear state
    this.isActive = false;
    this.loginTime = null;
    this.lastActivity = Date.now();
    this.lastStorageUpdate = Date.now();
    this.warningShown = false;
    this.inactivityWarningShown = false;

    // Clear storage
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    
    // Notify same-tab listeners before redirect
    window.dispatchEvent(new Event('loginStatusChanged'));
    localStorage.removeItem('username');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userStatus');
    localStorage.removeItem('sessionLoginTime');
    localStorage.removeItem('sessionLastActivity');
    localStorage.removeItem('sessionLastTokenUpdate');
    localStorage.removeItem('sessionRefreshLock');
    localStorage.removeItem('contribution_dismissed_at');
    
    // Mark as logged out to prevent duplicate handling
    sessionStorage.setItem('hasLoggedOut', 'true');

    // Check if we're on a public page — if so, skip the overlay and redirect immediately
    const publicPaths = ['/', '/home', '/login', '/register', '/register2', '/register3', '/register-interest', '/forgot-password', '/reset-password', '/verify-email', '/verify-email-sent', '/pricing', '/terms', '/privacy', '/refund', '/community-guidelines', '/cookie-policy', '/l3v3l-info', '/help'];
    const currentPath = window.location.pathname;
    const isPublicPage = publicPaths.some(path => currentPath === path || currentPath.startsWith(path + '/'));

    // Registration pages: stale tokens should be silently cleared — never redirect invitees to login
    const registrationPaths = ['/register', '/register2', '/register3', '/register-interest'];
    const isRegistrationPage = registrationPaths.some(path => currentPath === path || currentPath.startsWith(path + '/'));

    if (isRegistrationPage) {
      // Silently drop stale token — user is a new invitee, keep them on the registration page
      logger.debug('On registration page, silently clearing stale session without redirect');
      this.isLoggingOut = false;
      sessionStorage.removeItem('hasLoggedOut');
      return; // Do NOT redirect to /login
    } else if (isPublicPage) {
      // On other public pages, just redirect without showing overlay
      logger.debug('On public page, skipping session expired overlay');
      this.isLoggingOut = false;
      sessionStorage.removeItem('hasLoggedOut');
      window.location.href = '/login';
    } else {
      // Show session expired overlay on protected pages
      this.showSessionExpiredOverlay();

      // Redirect to login after brief delay for user to see message
      setTimeout(() => {
        this.isLoggingOut = false;
        sessionStorage.removeItem('hasLoggedOut');
        window.location.href = '/login';
      }, 1500);
    }

    logger.info('Session manager cleaned up - redirecting to login');
  }
  
  /**
   * Silent logout - clears tokens without showing overlay or redirecting
   * Used when session expires on public pages where user can continue browsing
   * @param {string} reason - Optional reason for logout for logging
   */
  silentLogout(reason = 'unknown') {
    logger.info(`SessionManager.silentLogout() triggered. Reason: ${reason}`);
    
    // Clear intervals
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
    
    if (this.inactivityInterval) {
      clearInterval(this.inactivityInterval);
      this.inactivityInterval = null;
    }

    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout);
      this.activityTimeout = null;
    }

    // Remove listeners
    this.removeActivityListeners();
    window.removeEventListener('storage', this.handleStorageChange);

    // Clear state
    this.isActive = false;
    this.loginTime = null;
    this.lastActivity = Date.now();
    this.lastStorageUpdate = Date.now();
    this.warningShown = false;
    this.inactivityWarningShown = false;
    this.isLoggingOut = false;

    // Clear storage (tokens and session data)
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('username');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userStatus');
    localStorage.removeItem('sessionLoginTime');
    localStorage.removeItem('sessionLastActivity');
    localStorage.removeItem('sessionLastTokenUpdate');
    localStorage.removeItem('sessionRefreshLock');
    localStorage.removeItem('contribution_dismissed_at');
    
    // Mark as logged out to prevent duplicate handling
    sessionStorage.setItem('hasLoggedOut', 'true');
    
    // Clear the hasLoggedOut flag after a short delay so user can log in again
    setTimeout(() => {
      sessionStorage.removeItem('hasLoggedOut');
    }, 1000);

    logger.info('Session silently cleared - user can continue browsing public pages');
  }
  
  /**
   * Show a full-screen overlay when session expires
   * This prevents the broken UI state
   */
  showSessionExpiredOverlay() {
    // Remove any existing overlay
    const existing = document.getElementById('session-expired-overlay');
    if (existing) {
      existing.remove();
    }
    
    // Create overlay
    const overlay = document.createElement('div');
    overlay.id = 'session-expired-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.85);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 999999;
      color: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;
    
    overlay.innerHTML = `
      <div style="text-align: center; padding: 40px;">
        <div style="font-size: 64px; margin-bottom: 20px;">🔒</div>
        <h2 style="font-size: 28px; margin-bottom: 16px; color: white;">Session Expired</h2>
        <p style="font-size: 16px; color: #ccc; margin-bottom: 24px;">Your session has ended. Redirecting to login...</p>
        <div style="width: 40px; height: 40px; border: 3px solid #666; border-top-color: #fff; border-radius: 50%; animation: spin 1s linear infinite;"></div>
      </div>
      <style>
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      </style>
    `;
    
    document.body.appendChild(overlay);
  }

  /**
   * Update login time (called after successful login)
   * Also persists to localStorage for cross-page-reload persistence
   */
  setLoginTime(time = Date.now()) {
    this.loginTime = time;
    this.lastActivity = time;
    this.warningShown = false;
    localStorage.setItem('sessionLoginTime', time.toString());
    localStorage.setItem('sessionLastActivity', time.toString());
    logger.debug('Login time set and persisted:', new Date(time).toISOString());
  }

  /**
   * Cleanup on app unmount
   */
  destroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout);
    }
    this.removeActivityListeners();
    this.isActive = false;
    logger.debug('Session manager destroyed');
  }
}

// Export singleton instance
const sessionManager = new SessionManager();
export default sessionManager;
